import { Component, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { WorkspaceService } from '../../../services/workspace.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../services/user.service';
import { BillingInterface, PhoneInterface, PlanInterface, UserInterface, WorkspaceInterface } from 'voxr-shared';
import { combineLatest } from 'rxjs';
import { BillingService } from '../../../services/billing.service';
import { ProgressBarModule } from 'primeng/progressbar';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { PhoneService } from '../../../services/phone.service';
import { MatCardModule } from '@angular/material/card';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DialogRef } from '@angular/cdk/dialog';

@Component({
  selector: 'app-select-workspace',
  standalone: true,
  imports: [
    CommonModule, RouterModule, FormsModule,
    ButtonModule, ProgressBarModule, DialogModule,
    MatToolbarModule, MatDialogModule, MatButtonModule, MatIconModule, 
    MatFormFieldModule, MatInputModule, MatSelectModule,
    MatCardModule,
  ],
  templateUrl: './select-workspace.component.html',
  styleUrl: './select-workspace.component.scss'
})
export class SelectWorkspaceComponent {
  private userService = inject(UserService);
  private workspaceService = inject(WorkspaceService);
  private phoneService = inject(PhoneService);
  private billingService = inject(BillingService);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  @Input() showInvited = true;

  loading = true;
  user?: UserInterface;
  billing?: BillingInterface;
  ownedWorkspaces?: WorkspaceInterface[];
  joinedWorkspaces?: WorkspaceInterface[];
  invitedWorkspaces?: WorkspaceInterface[];
  currentPlan?: PlanInterface;
  freeWorkspaces = 0;
  usedFreeWorkspaces = 0;
  maxWorkspaces?: number;
  canCreateWorkspace = false;
  onboarded = false;
  workspacePrice = 0;

  constructor() {
    combineLatest([this.userService.user$, this.workspaceService.workspaces$])
      .pipe(takeUntilDestroyed()).subscribe(([user, workspaces]) => {
        if (!user) return;
        this.load(user, workspaces);
      });
  }

  private async load(user: UserInterface, workspaces?: WorkspaceInterface[]) {
    try {
      this.loading = true;
      this.user = user;
      this.billing = this.user?._metadata?.billing as BillingInterface;
      this.onboarded = user.onboarded?.includes('complete') || false;

      this.ownedWorkspaces = workspaces?.filter(_ => _.ownerId?.toString() == user._id?.toString());
      const inviteds = workspaces?.filter(_ => !this.ownedWorkspaces?.find(o => _._id == o._id));
      this.joinedWorkspaces = inviteds?.filter(_ => _.members?.find(m => m.userId?.toString() == user._id?.toString()));
      this.invitedWorkspaces = inviteds?.filter(_ => !this.joinedWorkspaces?.some(j => j._id == _._id));

      if (this.billing?.planId) {
        this.currentPlan = await this.billingService.getPlan(this.billing.planId);
        this.freeWorkspaces = this.currentPlan?.workspacePrice?.tiers?.[0].up_to || 0;
        this.usedFreeWorkspaces = Math.min(this.freeWorkspaces, this.ownedWorkspaces?.length || 0);
        this.maxWorkspaces = this.currentPlan?.maxWorkspace || undefined;
        this.canCreateWorkspace = !this.maxWorkspaces || this.ownedWorkspaces?.length! < this.maxWorkspaces;
        if (this.canCreateWorkspace) this.workspacePrice = (this.currentPlan?.workspacePrice?.tiers?.[1]?.unit_amount || 0) / 100;
      }
    } finally {
      this.loading = false;
    }
  }

  async setWorkspace(workspace: WorkspaceInterface) {
    this.loading = true;
    // this.workspaceService.setWorkspace(workspace._id!.toString());
    await this.router.navigate(['/w', workspace._id, workspace.onboarded?.includes('completed') ? 'campaigns' : 'onboarding']);
    this.loading = false;
  }
  async joinWorkspace(workspace: WorkspaceInterface) {
    this.loading = true;
    try {
      await this.workspaceService.acceptInvitation(workspace._id!.toString());
      await this.userService.current();
      this.setWorkspace(workspace);
    } catch { this.loading = false; }
  }

  workspace?: Partial<WorkspaceInterface>;
  availablePhones?: PhoneInterface[];
  selectedPhonesIds?: string[];
  adminEmail?: string;
  loadingPhones = false;
  @ViewChild('newWorkspaceDialog') newWorkspaceDialog!: TemplateRef<any>;
  private newWorkspaceDialogRef?: MatDialogRef<any, any>;
  async newWorkspace() {
    delete this.availablePhones;
    this.newWorkspaceDialogRef = this.dialog.open(this.newWorkspaceDialog);
    this.workspace = { settings: {} };
    try {
      this.loadingPhones = true;
      this.availablePhones = await this.phoneService.getUnassignedPhones();
    } finally {
      this.loadingPhones = false;
    }
  }

  async createWorkspace() {
    if (!this.workspace) return;
    this.loading = true;
    try {
      const workspace = await this.workspaceService.create(this.workspace, this.adminEmail);
      if (this.selectedPhonesIds?.length)
        await this.phoneService.assignPhones(workspace._id!.toString(), this.selectedPhonesIds!);
      else
        if (!confirm('No phone number is assigned. Do you want to continue?')) return;

      this.newWorkspaceDialogRef?.close();
      this.router.navigate(['/w', workspace._id, 'onboarding']);
    } catch (error) {
    } finally {
      this.loading = false;
    }

  }

  async autoOnboard() {
    await this.billingService.autoOnboard();
  }
}
